import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { startWith } from 'rxjs/operators';

import { DefaultPrintFormatRequirementService } from '@core/services/default-print-format-requirement.service';
import { LabelFormatLookupService } from '@core/services/label-format-lookup.service';
import { StoreContextService } from '@core/services/store-context.service';
import { ToastService } from '@core/services/toast.service';

import { ModalComponent } from '../modal/modal.component';

/**
 * Mandatory, non-dismissible prompt shown once per store lacking a default
 * print format — see {@link DefaultPrintFormatRequirementService} for the
 * check that drives it. Rendered once at the app root
 * (`app.component.html`), not tied to any route, so it surfaces regardless
 * of which feature the user is on when the gap is found.
 */
@Component({
  selector: 'app-required-print-format-modal',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatSelectModule, ModalComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './required-print-format-modal.component.html',
  styleUrl: './required-print-format-modal.component.scss'
})
export class RequiredPrintFormatModalComponent {
  private readonly requirement = inject(DefaultPrintFormatRequirementService);
  private readonly storeCtx = inject(StoreContextService);
  protected readonly labelFormats = inject(LabelFormatLookupService);
  private readonly fb = inject(FormBuilder);
  private readonly toast = inject(ToastService);

  protected readonly open = computed(() => this.requirement.requiredStoreId() != null);
  protected readonly storeName = computed(() => {
    const id = this.requirement.requiredStoreId();
    return this.storeCtx.stores().find(s => s.id === id)?.name ?? 'this store';
  });

  protected readonly form = this.fb.nonNullable.group({
    printFormatId: [null as number | null, Validators.required]
  });

  private readonly formStatus = toSignal(this.form.statusChanges.pipe(startWith(this.form.status)), {
    initialValue: this.form.status
  });

  protected readonly submitting = this.requirement.submitting;
  protected readonly canSubmit = computed(() => this.formStatus() === 'VALID' && !this.submitting());

  constructor() {
    effect(() => {
      if (!this.open()) return;
      this.form.reset({ printFormatId: null });
      if (this.labelFormats.formats().length === 0 && this.labelFormats.loadStatus() === 'idle') {
        this.labelFormats.loadFormats().subscribe({
          error: () => this.toast.error("Couldn't load print formats", 'Please refresh the page.')
        });
      }
    });
  }

  protected onConfirm(): void {
    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      return;
    }
    const printFormatId = this.form.getRawValue().printFormatId!;
    this.requirement.configureDefault(
      printFormatId,
      () => this.toast.success('Default print format configured'),
      () => this.toast.error("Couldn't save the default print format", 'Please try again.')
    );
  }
}
